import { Icon } from '@fox/atoms/src/icon/index';
import { Select } from '@fox/atoms/src/select/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { AutoDataGrid } from '@fox/data-grid/src/auto-data-grid/index';
import { Flex, FlexRow } from '@fox/layout/src/flex';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useImportWizardContext } from './import-wizard';
import { Sheet } from './types';

export const DO_NOT_IMPORT = '!';
const ColumnLabelWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr;
	grid-template-areas: 'columnLabel sampleData ';
	gap: 1rem;
	align-items: center;
`;

export function ImportDataGrid({ ws }: { ws: Sheet }) {
	const { mapRef, profile } = useImportWizardContext() ?? {};

	const { hasHeader = true, skipRowsStart, skipRowsEnd, importLimit } = profile;

	const header = useMemo(() => ws.extractHeader(hasHeader, skipRowsStart), [ws, hasHeader, skipRowsStart]);

	const data = useMemo(() => {
		return ws.extractData({ hasHeader, skipRowsStart, skipRowsEnd, rowLimit: importLimit });
	}, [ws, hasHeader, skipRowsEnd, skipRowsStart, importLimit]);

	const newData = header.map(m => ({
		fileHeader: m,
		mappedColumn: mapRef[m],
		sampleData: data
			.map(d => d[m])
			.filter(d => !!d)
			.slice(0, 2)
	}));

	return (
		<AutoDataGrid
			id="importGrid"
			data={newData}
			loading={false}
			rowHeight={'60px'}
			getRowKey={row => row.fileHeader}
			hideColumnBorders={true}
			columns={[
				{
					initialWidth: 400,
					title: 'Spreadsheet column',
					dataIndex: 'fileHeader',
					render: (h, r) => (
						<>
							<ColumnLabelWrapper>
								<Typography variant="h6" style={{ gridArea: 'columnLabel' }}>
									{h}
								</Typography>
								<Flex style={{ flexDirection: 'column', gridArea: 'sampleData' }}>
									{r.sampleData.map(s => (
										<React.Fragment>
											<Typography>{s}</Typography>
										</React.Fragment>
									))}
								</Flex>
							</ColumnLabelWrapper>
						</>
					)
				},
				{
					initialWidth: 300,
					title: 'SwiftFox field',
					dataIndex: 'swiftfox_field',
					render: (_, { fileHeader: h }) => <HeaderChooser h={h} />
				},
				{
					initialWidth: 100,
					title: 'Mapped',
					fixed: 'right',
					dataIndex: 'mapped',
					render: (_, row) => {
						return (
							<>
								{row.mappedColumn ? (
									<FlexRow style={{ justifyContent: 'center' }}>
										<Icon iconName="CheckCircle" sx={{ fontSize: '2rem' }} color="primary" />
									</FlexRow>
								) : (
									<></>
								)}
							</>
						);
					}
				}
			]}
			total={data.length}
		/>
	);
}

const HeaderChooser = ({ h }: { h: string }) => {
	const { mapTypes, mapRef, setMapping } = useImportWizardContext();
	const mapTypesExtra = [...mapTypes, { title: 'Do Not Import', key: DO_NOT_IMPORT }].sort((a, b) => a.title.localeCompare(b.title));
	const hasGrouping = mapTypes.filter(f => f.grouping).length > 0;
	const mapTypesSorted = hasGrouping ? mapTypesExtra.sort((a, b) => (a.grouping ?? '')?.localeCompare(b.grouping ?? '')) : mapTypesExtra;
	return (
		<Select<{ title: string; key: string; grouping?: string }, false, false, false>
			options={mapTypesSorted}
			value={mapTypesExtra.find(f => f.key === mapRef[h]) ?? null}
			groupBy={o => (hasGrouping ? o.grouping ?? 'Default Fields' : undefined)}
			onChange={(_, v) => {
				setMapping(h, v?.key);
			}}
			getOptionDisabled={o => {
				return Object.values(mapRef).includes(o.key) && o.key !== DO_NOT_IMPORT;
			}}
			getOptionLabel={op => op.title}
			size="small"
		/>
	);
};

//OLD DATA GRID
// <AutoDataGrid
// 	id="importGrid"
// 	data={data}
// 	loading={false}
// 	getRowKey={row => `row_${data.indexOf(row)}`}
// 	columns={header.map(h => ({
// 		dataIndex: h,
// 		title: (
// 			<Select<{ title: string; key: string }, false, false, false>
// 				options={mapTypes}
// 				value={mapTypes.find(f => f.key === mapRef[h]) ?? null}
// 				onChange={(_, v) => setMapping(h, v.key)}
// 				getOptionLabel={op => op.title}
// 				size="small"
// 				label={h}
// 			/>
// 		),

// 		initialWidth: 200
// 	}))}
// 	total={data.length}
// />
